import RecentInspectionsList from '@components/Home/RecentInspectionsList';
import Header from '@components/Shared/Layout/Header';
import { useLocalization } from '@lib/localization';
import { Box, Pagination, Typography } from '@mui/material';
import { useGetRecentInspectionsQuery } from '@store/api/statisticEndpoints';
import { useLoading } from '@store/hooks';
import { useDebounce } from '@store/hooks/useDebounce';
import { useId, useEffect, useState } from 'react';

const ITEMS_PER_PAGE = 10;

/** How long a page change may take before the rows on screen are marked as stale. */
const PAGING_INDICATOR_DELAY = 300;

/**
 * Every inspection the reader may see, newest first — the list Home previews, at length.
 *
 * The page is the unit of fetching: each one is its own request and its own cache entry,
 * because the whole set is unbounded in a way Home's five never were. Rows are drawn by the
 * list Home draws, so following the heading out of Home lands on the same thing, only more
 * of it.
 */
const RecentInspectionsPage = () => {
  const { t } = useLocalization();
  const { show, hide } = useLoading();
  const componentId = useId();

  const [page, setPage] = useState(1);

  // `data` outlives an argument change — the previous page stays until the next arrives.
  const { data, isLoading, isFetching } = useGetRecentInspectionsQuery({ page, limit: ITEMS_PER_PAGE });

  /**
   * Rows from the page just left, under the number of the one asked for. Marked only once
   * the wait is long enough to notice: a response inside the delay — every one on a local
   * stack, most on a good network — swaps the rows before any indicator would have shown,
   * and one that flashed on for a few milliseconds would be a flicker of its own. It clears
   * the moment the rows land, not a delay later.
   */
  const isPaging = isFetching && !isLoading;
  const showPaging = useDebounce(isPaging, PAGING_INDICATOR_DELAY) && isPaging;

  // Only the first load takes the overlay: a page change swaps rows under a reader who is
  // already looking at the list, and covering it would be the loudest thing on the screen.
  useEffect(() => {
    (isLoading ? show : hide)(componentId);

    return () => hide(componentId);
  }, [componentId, hide, isLoading, show]);

  const total = data?.meta.total ?? 0;
  const pages = data?.meta.pages ?? 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Header />

      <RecentInspectionsList
        data={data?.data}
        loading={showPaging}
        action={total > 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('Inspections_Count', { count: total })}
          </Typography>
        )}
      />

      {pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={pages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}
    </Box>
  );
};

export default RecentInspectionsPage;
